import React, {memo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {isAlive} from 'mobx-state-tree';

import type {FullTextSearchResult} from '@stores/SearchStore';
import {useAppState, useRootStore, useUserState} from '@stores/AppStateContext';
import {useCrudServices} from '../../../platform/hooks/useServiceCombinations';

import {selectItemPlain} from '../../../actions/ui/selectItem';
import type {ItemModelType} from '@stores/WorkspaceStore';
import {ItemTrashMenuConfirmation} from './ItemTrashMenuConfirmation';
import {SearchContext} from './SearchContext';
import {ItemButtons} from './ItemButtons';
import {isPinned} from '../../../utils/isPinned';
import {clearAllTempState} from '../../../actions/ui/clearAllTempState';
import {highlightText} from '../../../utils/highlightText';
import {ItemLineActive} from './ItemLineActive';
import {pinItemUndoable, unPinItemUndoable} from '../../../actions/item/pin';
import {ItemState} from '../../components/Tree/types';
import {indentPerLevel} from '../../components/Tree/constants';
import {ItemLinePure} from '../../components/ItemLine/ItemLine';

const ICON_SIZE = 16;

export interface ItemLineProps {
    state?: ItemState;
    level: number;
    item: ItemModelType;
    index?: number;
    inEditorView?: boolean;
    searchResult?: FullTextSearchResult;
    highlight?: string;
    subtitle?: string | null;
    onContextMenu?: (event: React.MouseEvent) => void;
    parentsHash?: string | null;
    parentIdFromTree?: string | null;
}

export const ItemLine = memo(observer<ItemLineProps>(
    ({
        item,
        parentIdFromTree,
        level,
        state = 'idle',
        inEditorView = false,
        searchResult,
        highlight,
        subtitle,
        parentsHash,
        index,
        ...props
    }) => {
        const appState = useAppState();
        const rootStore = useRootStore();
        const userState = useUserState();
        const crudServices = useCrudServices();
        const activeWorkspace = rootStore.view.workspace!;
        const isSelected = activeWorkspace.selectedId === item.id;
        const [isMoreClicked, setMoreClicked] = useState(false);

        const isInSearch = !!(searchResult || highlight);
        const showExtendIcon = item.isCluster && !isInSearch;
        const isReader = userState!.isCurrentUserReadOnly;

        const _isPinned = !searchResult && level === 0 && isPinned(item);

        let paddingLeft = level * indentPerLevel;
        if (!showExtendIcon) {
            paddingLeft = paddingLeft + ICON_SIZE;
        }

        const showConfirmDelete = (
            activeWorkspace.confirmDeleteOpenUnder !== undefined
            && activeWorkspace.confirmDeleteOpenUnder === `${parentIdFromTree}/${item.id}`
        );

        const scrollIntoView = activeWorkspace.scrollIntoViewId === item.id;
        const _itemName = item.getNameWithPlaceholder(rootStore.currentLocale);

        const handleExpandToggle = (event: React.SyntheticEvent) => {
            item.setExpanded(!item.isExpanded);
            event.stopPropagation();
        };

        const handlePinToggle = (event: React.SyntheticEvent) => {
            if (item.pinned) {
                unPinItemUndoable(crudServices, userState!.dsManager!, item.id, activeWorkspace);
            } else {
                pinItemUndoable(crudServices, userState!.dsManager!, item.id, activeWorkspace);
            }
        };

        const handleSelect = () => {
            if (isSelected) {
                if (appState.mobileNavController.isMobile && !inEditorView) {
                    appState.mobileNavController.openPage();
                } else {
                    item.setExpanded(!item.isExpanded);
                }
            } else {
                selectItemPlain(rootStore, activeWorkspace, item.id);
            }
            if (!showConfirmDelete) {
                clearAllTempState(rootStore);
            }
        };

        const handleContextMenu = (event: React.MouseEvent) => {
            appState.addAdditionalContextMenu({
                clientX: event.clientX,
                clientY: event.clientY,
                type: 'item',
                id: item.id,
                params: {
                    itemId: item.id,
                    parentIdFromTree,
                    inEditorView,
                    index,
                },
            });
        };

        return (
            <ItemLinePure
                id={item.id}
                name={highlight ? highlightText(highlight, _itemName) : _itemName}
                isCluster={item.isCluster}
                isExpanded={item.isExpanded}
                isPinned={_isPinned}
                isSelected={isSelected}
                isReadOnly={isReader}
                isInSearch={isInSearch}
                level={level}
                paddingLeft={paddingLeft}
                state={state}
                inEditorView={inEditorView}
                subtitle={subtitle}
                showConfirmDelete={showConfirmDelete}
                scrollIntoView={scrollIntoView}
                isMoreClicked={isMoreClicked}
                parentsHash={parentsHash}
                onSelect={handleSelect}
                onExpandToggle={handleExpandToggle}
                onPinToggle={handlePinToggle}
                onContextMenu={handleContextMenu}
                buttonsSlot={
                    <ItemButtons
                        key={`${parentIdFromTree}-${item.id}`}
                        item={item}
                        parentIdFromTree={parentIdFromTree}
                        index={index}
                        isInSearch={isInSearch}
                        inEditorView={inEditorView}
                        isMoreClicked={isMoreClicked}
                        setMoreClicked={setMoreClicked}
                    />
                }
                activeUsersSlot={<ItemLineActive item={item}/>}
                searchContextSlot={searchResult ? <SearchContext searchResult={searchResult}/> : undefined}
                confirmDeleteSlot={
                    <ItemTrashMenuConfirmation
                        item={item}
                        parentIdFromTree={parentIdFromTree}
                        className="ItemLine_delete"
                    />
                }
            />
        );
    }
));
